// src/components/Students.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Pagination,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  CloudDownload as CloudDownloadIcon,
  Layers as LayersIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
  TrendingUp as PromoteIcon,
  ToggleOn as StatusIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { studentAPI, academicAPI, BASE_URL } from "../../services/api";
import { useNavigate } from "react-router-dom";
import StudentImportExport from "./StudentImportExport.js";
import { useAccessControl } from "../../hooks/useAccessControl";
import PermissionGuard from "../access-control/PermissionGuard";
import { usePermissions, RESOURCES, ACTIONS } from "../../hooks/usePermissions";

const Students = ({ setCurrentPage }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } = usePermissions();
  const { can } = useAccessControl();
  const canReadStudents = can(RESOURCES.STUDENTS, ACTIONS.READ);

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });
  const [genderFilter, setGenderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("1");
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [streamFilter, setStreamFilter] = useState("");
  const [viewingStudent, setViewingStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [allStreams, setAllStreams] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);

  const [bulkStudents, setBulkStudents] = useState([
    {
      lin: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      class_stream_id: "",
      gender: "male",
      birthday: "",
      admission_date: "",
      address: "",
      guardian_name: "",
      guardian_phone: "",
      guardian_email: "",
      guardian_relationship: "",
      active: true,
    },
  ]);
  const [formData, setFormData] = useState({
    lin: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    class_stream_id: "",
    gender: "male",
    birthday: "",
    admission_date: "",
    address: "",
    guardian_name: "",
    guardian_phone: "",
    guardian_email: "",
    guardian_relationship: "",
    picture: null,
    index_number: "",
    schoolpay_payment_code: "",
    active: true,
  });
  const [picturePreview, setPicturePreview] = useState(null);

  const getImageUrl = (pictureUrl) => {
    if (!pictureUrl || pictureUrl.includes('image_holder.jpg') || pictureUrl === 'null') return null;
    if (pictureUrl.startsWith('http')) return pictureUrl;
    return `${BASE_URL}${pictureUrl.startsWith('/') ? pictureUrl : '/' + pictureUrl}`;
  };

  useEffect(() => {
    if (canReadStudents) {
      fetchStudents();
      fetchClasses();
      fetchAllStreams();
      fetchClassStreams();
    }
  }, [canReadStudents]);

  const fetchClassStreams = async () => {
    try {
      const response = await academicAPI.getClassStreams();
      setClassStreams(response.data || []);
    } catch (error) {
      console.error("Error fetching class streams:", error);
      setClassStreams([]);
    }
  };

  const fetchStudents = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(classFilter && { class_id: classFilter }),
        ...(streamFilter && { stream_id: streamFilter }),
        ...(genderFilter && { gender: genderFilter }),
        ...(statusFilter && { active: statusFilter }),
        ...filters,
      };

      // Remove empty params
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === undefined) {
          delete params[key];
        }
      });

      console.log("Fetching with params:", params);
      let response;

      try {
        response = await studentAPI.getAll(params);
      } catch (error) {
        // If pagination fails, try without pagination
        console.log("Pagination failed, trying without params:", error);
        response = await studentAPI.getAll();
      }

      console.log("Students response:", response.data);
      
      // Debug: Check picture URLs
      if (response.data?.data) {
        response.data.data.forEach(student => {
          if (student.picture_url) {
            console.log(`Student ${student.full_name} picture URL:`, student.picture_url);
          }
        });
      }

      if (
        response.data &&
        typeof response.data === "object" &&
        response.data.data
      ) {
        // Paginated response from Laravel
        setStudents(response.data.data);
        setFilteredStudents(response.data.data);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total,
        });
      } else if (Array.isArray(response.data)) {
        // Non-paginated response (array)
        setStudents(response.data);
        setFilteredStudents(response.data);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.data.length,
          total: response.data.length,
        });
      } else {
        console.error("Unexpected response format:", response.data);
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await studentAPI.getClasses();
      setClasses(response.data || response || []);
    } catch (error) {
      console.error("Error fetching classes from API:", error);
      setClasses([]);
    }
  };

  const fetchStreams = async (classId) => {
    try {
      const response = await studentAPI.getStreams(classId);
      setStreams(response.data);
    } catch (error) {
      console.error("Error fetching streams:", error);
    }
  };

  const fetchAllStreams = async () => {
    try {
      const response = await academicAPI.getStreams();
      setAllStreams(response.data || []);
    } catch (error) {
      console.error("Error fetching all streams:", error);
      setAllStreams([]);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStudents(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, classFilter, streamFilter, genderFilter, statusFilter]);

  const handlePageChange = (page) => {
    fetchStudents(page, {
      search: searchTerm,
      class_id: classFilter,
      stream_id: streamFilter,
      gender: genderFilter,
      active: statusFilter,
    });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, per_page: newSize, current_page: 1 }));
    // Fetch immediately with new page size
    const params = {
      page: 1,
      per_page: newSize,
      ...(searchTerm && { search: searchTerm }),
      ...(classFilter && { class_id: classFilter }),
      ...(streamFilter && { stream_id: streamFilter }),
      ...(genderFilter && { gender: genderFilter }),
      ...(statusFilter && { active: statusFilter }),
    };
    
    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === undefined) {
        delete params[key];
      }
    });
    
    setLoading(true);
    studentAPI.getAll(params).then(response => {
      if (response.data && typeof response.data === "object" && response.data.data) {
        setStudents(response.data.data);
        setFilteredStudents(response.data.data);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total,
        });
      }
    }).finally(() => setLoading(false));
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      try {
        await studentAPI.delete(id);
        fetchStudents(pagination.current_page);
      } catch (error) {
        console.error("Error deleting student:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingStudent) {
        // Update student data without picture first
        const updateData = { ...formData };
        delete updateData.picture;
        await studentAPI.update(editingStudent.id, updateData);
        
        // If there's a new picture file, upload it separately
        if (formData.picture instanceof File) {
          const pictureFormData = new FormData();
          pictureFormData.append('picture', formData.picture);
          await studentAPI.uploadProfilePicture(editingStudent.id, pictureFormData);
        }
      } else {
        // For new students, create first then upload picture if exists
        const createData = { ...formData };
        delete createData.picture;
        const response = await studentAPI.create(createData);
        
        // If there's a picture, upload it
        if (formData.picture instanceof File && response.data.id) {
          const pictureFormData = new FormData();
          pictureFormData.append('picture', formData.picture);
          await studentAPI.uploadProfilePicture(response.data.id, pictureFormData);
        }
      }
      
      fetchStudents(pagination.current_page);
      resetForm();
      setSnackbar({ open: true, message: 'Student saved successfully!', severity: 'success' });
    } catch (error) {
      console.error("Error saving student:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
        setSnackbar({ open: true, message: errorMessages, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || error.message, severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      lin: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      class_stream_id: "",
      gender: "male",
      birthday: "",
      admission_date: "",
      address: "",
      guardian_name: "",
      guardian_phone: "",
      guardian_email: "",
      guardian_relationship: "",
      picture: null,
      index_number: "",
      schoolpay_payment_code: "",
      active: true,
    });
    setPicturePreview(null);
    setShowForm(false);
    setEditingStudent(null);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await studentAPI.bulkCreate({ students: bulkStudents });
      fetchStudents(1);
      setShowBulkForm(false);
      setBulkStudents([
        {
          lin: "",
          first_name: "",
          middle_name: "",
          last_name: "",
          class_stream_id: "",
          gender: "male",
          birthday: "",
          admission_date: "",
          address: "",
          guardian_name: "",
          guardian_phone: "",
          guardian_email: "",
          guardian_relationship: "",
          active: true,
        },
      ]);
      setSnackbar({ open: true, message: 'Students created successfully!', severity: 'success' });
    } catch (error) {
      console.error("Error creating bulk students:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
        setSnackbar({ open: true, message: errorMessages, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || error.message, severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const addBulkStudent = () => {
    setBulkStudents([
      ...bulkStudents,
      {
        lin: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        class_stream_id: "",
        gender: "male",
        birthday: "",
        admission_date: "",
        address: "",
        guardian_name: "",
        guardian_phone: "",
        guardian_email: "",
        guardian_relationship: "",
        active: true,
      },
    ]);
  };

  const removeBulkStudent = (index) => {
    if (bulkStudents.length > 1) {
      setBulkStudents(bulkStudents.filter((_, i) => i !== index));
    }
  };

  const updateBulkStudent = (index, field, value) => {
    const updated = [...bulkStudents];
    updated[index][field] = value;
    setBulkStudents(updated);
  };

  const handleEdit = (student) => {
    console.log('Editing student:', student);
    console.log('Student picture URL:', student.picture_url);
    setFormData(student);
    // Set picture preview to existing picture URL if it exists and is not placeholder
    if (student.picture_url && !student.picture_url.includes('image_holder.jpg')) {
      setPicturePreview(getImageUrl(student.picture_url));
    } else {
      setPicturePreview(null);
    }
    setEditingStudent(student);
    setShowForm(true);
  };

  const handleView = async (student) => {
    console.log("Viewing student:", student);
    setViewingStudent(student);
    try {
      const response = await studentAPI.getById(student.id);
      console.log("Student details:", response.data);
      setStudentDetails(response.data);
    } catch (error) {
      console.error("Error fetching student details:", error);
    }
  };

  const handleCollectFees = (student) => {
    // Store student data for fee collection and navigate
    localStorage.setItem("selectedStudent", JSON.stringify(student));
    if (setCurrentPage) {
      setCurrentPage("fees-payments");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setClassFilter("");
    setStreamFilter("");
    setGenderFilter("");
    setStatusFilter("1"); // Reset to active students only
    fetchStudents(1, { search: "", class_id: "", stream_id: "", gender: "", active: "1" });
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (classFilter) params.class_id = classFilter;
      if (streamFilter) params.stream_id = streamFilter;
      if (genderFilter) params.gender = genderFilter;
      if (statusFilter) params.active = statusFilter;

      const response = await studentAPI.downloadStudentsPdf(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `students_list_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSnackbar({ open: true, message: 'Failed to download PDF', severity: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!canReadStudents) return <Alert severity="error">You do not have permission to view students.</Alert>;

  if (loading) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Box>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={140}
            height={36}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Form Skeleton */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Skeleton
                    variant="rectangular"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
              ))}
            </Grid>
            <Box mt={2} display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width={80}
                height={36}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={36}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Table Skeleton */}
        <Card>
          <CardContent>
            <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Name",
                      "Register No",
                      "Class",
                      "Guardian",
                      "Phone",
                      "Status",
                      "Actions",
                    ].map((header, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={80} height={16} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton variant="text" width={120} height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={60} height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={90} height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          variant="rectangular"
                          width={60}
                          height={20}
                          sx={{ borderRadius: 3 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="circular" width={24} height={24} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              Students Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage student records and information
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent={{ xs: "stretch", md: "flex-end" }}
              mt={{ xs: 2, md: 0 }}
            >
              <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.CREATE}>
                <Button
                  variant={showForm ? "outlined" : "contained"}
                  startIcon={<AddIcon />}
                  onClick={() => setShowForm(!showForm)}
                  size="small"
                  fullWidth={isMobile}
                >
                  {showForm ? "Cancel" : "Add Student"}
                </Button>
              </PermissionGuard>
              <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.CREATE}>
                <Button
                  variant={showBulkForm ? "outlined" : "text"}
                  startIcon={<LayersIcon />}
                  onClick={() => setShowBulkForm(!showBulkForm)}
                  size="small"
                  fullWidth={isMobile}
                >
                  {showBulkForm ? "Cancel" : "Bulk Add"}
                </Button>
              </PermissionGuard>
              <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
                <Button
                  variant="text"
                  startIcon={<PromoteIcon />}
                  onClick={() => navigate('/students/promote')}
                  size="small"
                  fullWidth={isMobile}
                  color="success"
                >
                  Promote
                </Button>
              </PermissionGuard>
              <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
                <Button
                  variant="text"
                  startIcon={<StatusIcon />}
                  onClick={() => navigate('/students/deactivate')}
                  size="small"
                  fullWidth={isMobile}
                  color="warning"
                >
                  Bulk Status
                </Button>
              </PermissionGuard>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <CloudDownloadIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" color="text.secondary">
                Import/Export Students
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {(canExport(RESOURCES.STUDENTS) || canRead(RESOURCES.STUDENTS)) && (
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  size="small"
                >
                  {downloadingPdf ? 'Downloading...' : 'Export PDF'}
                </Button>
              )}
              {(canImport(RESOURCES.STUDENTS) || canCreate(RESOURCES.STUDENTS)) && (
                <StudentImportExport 
                  onImportComplete={() => fetchStudents(1)} 
                  filters={{
                    search: searchTerm,
                    class_id: classFilter,
                    stream_id: streamFilter,
                    gender: genderFilter,
                    active: statusFilter
                  }}
                />
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name or registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            {/* Option 1: Filter by Class */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={classFilter}
                  onChange={(e) => {
                    setClassFilter(e.target.value);
                    setStreamFilter(""); // Clear stream filter when class changes
                  }}
                  displayEmpty
                >
                  <MenuItem value="">All Classes</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name} ({cls.level?.name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Option 2: Filter by Stream */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={streamFilter}
                  onChange={(e) => setStreamFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Streams</MenuItem>
                  {Array.isArray(allStreams) &&
                    allStreams.map((stream) => (
                      <MenuItem key={stream.id} value={stream.id}>
                        {stream.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filter by Gender */}
            <Grid item xs={12} sm={6} md={1.5}>
              <FormControl fullWidth size="small">
                <Select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Genders</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Filter by Status */}
            <Grid item xs={12} sm={6} md={1.5}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="1">Active Only</MenuItem>
                  <MenuItem value="0">Inactive Only</MenuItem>
                  <MenuItem value="">All Students</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                spacing={1}
              >
                {(searchTerm || classFilter || streamFilter || genderFilter || statusFilter !== "1") && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    fullWidth={isMobile}
                  >
                    Clear All
                  </Button>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                >
                  <PeopleIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total:{" "}
                    <Typography
                      component="span"
                      color="primary"
                      fontWeight="bold"
                    >
                      {pagination.total}
                    </Typography>{" "}
                    students
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog
        open={showForm}
        onClose={resetForm}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              {editingStudent ? "Edit Student" : "Add New Student"}
            </Typography>
            {isMobile && (
              <IconButton onClick={resetForm} size="small">
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="LIN (Learner Identification Number)"
                    value={formData.lin}
                    onChange={(e) =>
                      setFormData({ ...formData, lin: e.target.value })
                    }
                    placeholder="e.g., LIN2024001"
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Index Number (Optional)"
                    value={formData.index_number}
                    onChange={(e) =>
                      setFormData({ ...formData, index_number: e.target.value })
                    }
                    placeholder="e.g., U0123/456/2024"
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="SchoolPay Payment Code (Optional)"
                    value={formData.schoolpay_payment_code}
                    onChange={(e) =>
                      setFormData({ ...formData, schoolpay_payment_code: e.target.value })
                    }
                    placeholder="e.g., 1006480152"
                    helperText="Assigned by SchoolPay — used to auto-match fee payments"
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="picture-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormData({ ...formData, picture: file });
                          setPicturePreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                    {picturePreview ? (
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <img 
                          src={picturePreview} 
                          alt="Preview" 
                          style={{ 
                            width: '200px', 
                            height: '200px', 
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            objectFit: 'cover'
                          }}
                        />
                        <Box flex={1}>
                          <label htmlFor="picture-upload">
                            <Button variant="outlined" component="span" fullWidth>
                              Change Picture
                            </Button>
                          </label>
                          <Button 
                            size="small" 
                            color="error" 
                            fullWidth
                            sx={{ mt: 1 }}
                            onClick={async () => {
                              if (editingStudent && editingStudent.id) {
                                try {
                                  await studentAPI.deleteProfilePicture(editingStudent.id);
                                  setSnackbar({ open: true, message: 'Profile picture removed successfully!', severity: 'success' });
                                } catch (error) {
                                  console.error('Error deleting profile picture:', error);
                                  setSnackbar({ open: true, message: 'Failed to remove profile picture', severity: 'error' });
                                }
                              }
                              setFormData({ ...formData, picture: null });
                              setPicturePreview(null);
                            }}
                          >
                            Remove Picture
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <label htmlFor="picture-upload">
                        <Button variant="outlined" component="span" fullWidth>
                          Upload Picture
                        </Button>
                      </label>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
                Personal Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    placeholder="e.g., John"
                    required
                  />
                </Grid>
                <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Middle Name"
                    value={formData.middle_name}
                    onChange={(e) =>
                      setFormData({ ...formData, middle_name: e.target.value })
                    }
                    placeholder="e.g., Paul"
                  />
                </Grid>
                <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    placeholder="e.g., Doe"
                    required
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender}
                      label="Gender"
                      onChange={(e) =>
                        setFormData({ ...formData, gender: e.target.value })
                      }
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Birthday"
                      value={formData.birthday ? dayjs(formData.birthday) : null}
                      onChange={(newValue) => setFormData({ ...formData, birthday: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                      slotProps={{ textField: { fullWidth: true, size: "medium" } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Class & Stream</InputLabel>
                    <Select
                      value={formData.class_stream_id || ""}
                      label="Class & Stream"
                      onChange={(e) =>
                        setFormData({ ...formData, class_stream_id: e.target.value })
                      }
                    >
                      <MenuItem value="" disabled>
                        Select Class & Stream
                      </MenuItem>
                      {classStreams.map((classStream) => (
                        <MenuItem key={classStream.id} value={classStream.id}>
                          {classStream.display_name || `${classStream.school_class?.name}-${classStream.stream?.name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Admission Date"
                      value={formData.admission_date ? dayjs(formData.admission_date) : null}
                      onChange={(newValue) => setFormData({ ...formData, admission_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                      slotProps={{ textField: { fullWidth: true, size: "medium", required: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                {editingStudent && (
                  <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.active || false}
                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              Student Status
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formData.active ? 'Active - Student is currently enrolled' : 'Inactive - Student is not currently enrolled'}
                            </Typography>
                          </Box>
                        }
                        labelPlacement="end"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
                Guardian Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Guardian Name"
                    value={formData.guardian_name}
                    onChange={(e) =>
                      setFormData({ ...formData, guardian_name: e.target.value })
                    }
                    placeholder="e.g., Jane Doe"
                    required
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Guardian Phone"
                    value={formData.guardian_phone}
                    onChange={(e) =>
                      setFormData({ ...formData, guardian_phone: e.target.value })
                    }
                    placeholder="e.g., +256700123456"
                    required
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Guardian Email (Optional)"
                    value={formData.guardian_email}
                    onChange={(e) =>
                      setFormData({ ...formData, guardian_email: e.target.value })
                    }
                    placeholder="e.g., jane.doe@email.com"
                  />
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                      value={formData.guardian_relationship || ""}
                      label="Relationship"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          guardian_relationship: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      <MenuItem value="father">Father</MenuItem>
                      <MenuItem value="mother">Mother</MenuItem>
                      <MenuItem value="guardian">Guardian</MenuItem>
                      <MenuItem value="uncle">Uncle</MenuItem>
                      <MenuItem value="aunt">Aunt</MenuItem>
                      <MenuItem value="grandparent">Grandparent</MenuItem>
                      <MenuItem value="sibling">Sibling</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    placeholder="Enter guardian's address"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, flexDirection: isMobile ? "column" : "row", gap: 1 }}
        >
          <Button onClick={resetForm} fullWidth={isMobile} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth={isMobile}
            disabled={saving}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {editingStudent ? "Updating..." : "Saving..."}
              </Box>
            ) : (editingStudent ? "Update" : "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showBulkForm}
        onClose={() => setShowBulkForm(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Add Multiple Students</Typography>
            {isMobile && (
              <IconButton onClick={() => setShowBulkForm(false)} size="small">
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleBulkSubmit} sx={{ pt: 1 }}>
            {bulkStudents.map((student, index) => (
              <Card key={index} sx={{ mb: 2, p: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Student {index + 1}</Typography>
                  {bulkStudents.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeBulkStudent(index)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="LIN"
                      value={student.lin}
                      onChange={(e) =>
                        updateBulkStudent(index, "lin", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={student.first_name}
                      onChange={(e) =>
                        updateBulkStudent(index, "first_name", e.target.value)
                      }
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      value={student.middle_name}
                      onChange={(e) =>
                        updateBulkStudent(index, "middle_name", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={student.last_name}
                      onChange={(e) =>
                        updateBulkStudent(index, "last_name", e.target.value)
                      }
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth required size="small">
                      <Select
                        value={student.class_stream_id || ""}
                        label="Class & Stream"
                        onChange={(e) =>
                          updateBulkStudent(index, "class_stream_id", e.target.value)
                        }
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Class & Stream
                        </MenuItem>
                        {classStreams.map((classStream) => (
                          <MenuItem key={classStream.id} value={classStream.id}>
                            {classStream.display_name || `${classStream.school_class?.name}-${classStream.stream?.name}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth required size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={student.gender}
                        label="Gender"
                        onChange={(e) =>
                          updateBulkStudent(index, "gender", e.target.value)
                        }
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Admission Date"
                        value={student.admission_date ? dayjs(student.admission_date) : null}
                        onChange={(newValue) =>
                          updateBulkStudent(
                            index,
                            "admission_date",
                            newValue ? newValue.format('YYYY-MM-DD') : ''
                          )
                        }
                        slotProps={{ textField: { fullWidth: true, size: "small", required: true } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Guardian Name"
                      value={student.guardian_name}
                      onChange={(e) =>
                        updateBulkStudent(
                          index,
                          "guardian_name",
                          e.target.value
                        )
                      }
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Guardian Phone"
                      value={student.guardian_phone}
                      onChange={(e) =>
                        updateBulkStudent(
                          index,
                          "guardian_phone",
                          e.target.value
                        )
                      }
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Guardian Email"
                      value={student.guardian_email}
                      onChange={(e) =>
                        updateBulkStudent(
                          index,
                          "guardian_email",
                          e.target.value
                        )
                      }
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Box textAlign="center" mb={3}>
              <Button variant="outlined" onClick={addBulkStudent}>
                Add Another Student
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, flexDirection: isMobile ? "column" : "row", gap: 1 }}
        >
          <Button onClick={() => setShowBulkForm(false)} fullWidth={isMobile} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkSubmit}
            variant="contained"
            fullWidth={isMobile}
            disabled={saving}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : "Save All Students"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewingStudent}
        onClose={() => {
          setViewingStudent(null);
          setStudentDetails(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              Student Details - {viewingStudent?.first_name}{" "}
              {viewingStudent?.last_name}
            </Typography>
            {isMobile && (
              <IconButton
                onClick={() => {
                  setViewingStudent(null);
                  setStudentDetails(null);
                }}
                size="small"
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewingStudent && (
            <>
              {
                <Card elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Grid container spacing={4}>
                    {viewingStudent?.picture_url && !viewingStudent.picture_url.includes('image_holder.jpg') && (
                      <Grid item xs={12}>
                        <Box textAlign="center" mb={2}>
                          <img 
                            src={getImageUrl(viewingStudent.picture_url)} 
                            alt={viewingStudent.full_name}
                            style={{ 
                              width: '150px',
                              height: '150px',
                              borderRadius: '50%',
                              border: `3px solid ${theme.palette.primary.main}`,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              console.log('Image failed to load:', getImageUrl(viewingStudent.picture_url));
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => {
                              console.log('Image loaded successfully:', getImageUrl(viewingStudent.picture_url));
                            }}
                          />
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PeopleIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" color="primary">
                          Personal Information
                        </Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Register No:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.registration_no ||
                              viewingStudent?.lin ||
                              "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Index Number:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.index_number || "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Full Name:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.first_name}{" "}
                            {viewingStudent?.middle_name
                              ? viewingStudent.middle_name + " "
                              : ""}
                            {viewingStudent?.last_name}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Class:
                          </Typography>
                          <Chip
                            label={viewingStudent?.class_stream_name || "N/A"}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Level:
                          </Typography>
                          <Chip
                            label={
                              viewingStudent?.class_stream?.school_class?.level
                                ?.name || "N/A"
                            }
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Gender:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {viewingStudent?.gender || "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Birthday:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.birthday
                              ? new Date(
                                  viewingStudent.birthday
                                ).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Admission:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.admission_date
                              ? new Date(
                                  viewingStudent.admission_date
                                ).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Status:
                          </Typography>
                          <Chip
                            label={
                              viewingStudent?.active ? "Active" : "Inactive"
                            }
                            color={viewingStudent?.active ? "success" : "error"}
                            size="small"
                            sx={{ fontWeight: "medium" }}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PeopleIcon color="secondary" sx={{ mr: 1 }} />
                        <Typography variant="h6" color="secondary">
                          Guardian Information
                        </Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Name:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.guardian_name || "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Relationship:
                          </Typography>
                          <Chip
                            label={
                              viewingStudent?.guardian_relationship || "N/A"
                            }
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "capitalize" }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Phone:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.guardian_phone || "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 120 }}
                          >
                            Email:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {viewingStudent?.guardian_email || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Address:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ wordBreak: "break-word" }}
                          >
                            {viewingStudent?.address || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Card>
              }
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setViewingStudent(null);
              setStudentDetails(null);
            }}
            fullWidth={isMobile}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" display="flex" alignItems="center">
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              Students List
            </Typography>
          </Box>

          {isMobile ? (
            // Mobile Card Layout
            <Box>
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <Card
                    key={student.id}
                    sx={{ 
                      mb: 2, 
                      border: "1px solid", 
                      borderColor: "divider",
                      opacity: student.active ? 1 : 0.7,
                      backgroundColor: student.active ? 'inherit' : 'action.hover'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="primary"
                          >
                            {student.last_name} {student.first_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {student.registration_no || student.lin}
                          </Typography>
                        </Box>
                        <Chip
                          label={student.active ? "Active" : "Inactive"}
                          color={student.active ? "success" : "error"}
                          size="small"
                          variant={student.active ? "filled" : "outlined"}
                        />
                      </Box>

                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Class
                          </Typography>
                          <Typography variant="body2">
                            {student.class_stream_name || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Gender
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {student.gender}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Guardian
                          </Typography>
                          <Typography variant="body2">
                            {student.guardian_name}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 1 }} />

                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Student Ledger">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() =>
                              navigate(`/students/ledger/${student.id}`)
                            }
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Student">
                          <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(student)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </PermissionGuard>
                        </Tooltip>
                        <Tooltip title="Collect Fees">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleCollectFees(student)}
                          >
                            <PaymentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No students found
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            // Desktop Table Layout
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Register No</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Class</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Stream</TableCell>
                    {!isTablet && (
                      <TableCell sx={{ minWidth: 80 }}>Gender</TableCell>
                    )}
                    <TableCell sx={{ minWidth: 150 }}>Guardian</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 150, width: 150 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <TableRow 
                        key={student.id} 
                        hover
                        sx={{
                          opacity: student.active ? 1 : 0.7,
                          backgroundColor: student.active ? 'inherit' : 'action.hover'
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight="medium"
                          >
                            {student.registration_no || student.lin}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {student.picture_url && !student.picture_url.includes('image_holder.jpg') && (
                              <img 
                                src={getImageUrl(student.picture_url)} 
                                alt={student.full_name}
                                style={{ 
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0'
                                }}
                                onError={(e) => {
                                  console.log('Table image failed to load:', getImageUrl(student.picture_url));
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('Table image loaded successfully:', getImageUrl(student.picture_url));
                                }}
                              />
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {student.last_name} {student.first_name}
                              </Typography>
                              {isTablet && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {student.gender}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.school_class.name || "N/A"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.class_stream.stream.name || "N/A"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        {!isTablet && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {student.gender}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {student.guardian_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.active ? "Active" : "Inactive"}
                            color={student.active ? "success" : "error"}
                            size="small"
                            variant={student.active ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Student Details">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleView(student)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Student Ledger">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() =>
                                  navigate(`/students/ledger/${student.id}`)
                                }
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Student">
                              <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(student)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </PermissionGuard>
                            </Tooltip>
                            <Tooltip title="Collect Fees">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleCollectFees(student)}
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={isTablet ? 6 : 7}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No students found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.last_page > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing{" "}
                  {pagination.total === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total)}{" "}
                  of {pagination.total} entries
                </Typography>
                
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={pagination.per_page}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      sx={{ minWidth: 70 }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={(event, page) => handlePageChange(page)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 2}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Students;
